/**
 * FluffCo Cart Drawer — fluffco-cart.js
 * Vanilla JS, zero dependencies. Pixel parity with manus CartFlyout.tsx.
 *
 * Speed contract:
 *  - If cart is already cached, drawer opens & renders in the same frame.
 *  - Network calls (qty change, remove) render optimistically then confirm.
 *  - GWP is never auto-added to the live cart; it is added silently at checkout.
 */
(function () {
  'use strict';

  // ─── CDN image registry (mirrors productImages.ts + quickadd snippet) ────────
  var CDN = '/cdn/shop/t/166/assets';
  var CDN_IMAGES = {
    'hotel-towel':                                    CDN + '/HotelTowel_1_780227d3.webp',
    'hotel-slippers':                                 CDN + '/HotelSlippers_1_0d5de69f.webp',
    'silk-pillowcase':                                CDN + '/fluffco-silk-pillowcase-ivory-flat_da58e1cc.webp',
    'silk-eyemask':                                   CDN + '/fluffco-silk-eyemask-cream-flat_01e623eb.webp',
    'linen-spray':                                    CDN + '/LinenSpray_1_d712dcac.png',
    'room-spray-1':                                   CDN + '/RoomSpray_1_783063cd.png',
    'hotel-waffle-robe':                              CDN + '/HotelWaffleRobe_1_142fb37a.webp',
    'hotel-lounge-robe':                              CDN + '/HotelLoungeRobe_1_b1df9a64.webp',
    'pillow-comforter-kit':                           CDN + '/PillowComforter_1_b1edc7cb.png',
    'duvet-cover':                                    CDN + '/fluffco-duvet-cover-folded-tan-bg-1_a7c761c2.webp',
    'flat-fitted-sheet':                              CDN + '/fluffco-flat-fitted-sheet-folded-white-1_31a5ab10.webp',
    'down-alternative-comforter':                     CDN + '/fluffco-down-alternative-comforter-folded-tan-bg_9e9f5372.webp',
    'down-blended-comforter-1':                       CDN + '/fluffco-down-blended-comforter-white-folded_c38bda34.webp',
    'hotel-pillowcase-set-3':                         CDN + '/fluffco-pillowcase-set-folded-tan-bg-1_cc5f8898.webp',
    'sheet-kit':                                      CDN + '/SheetsKit_1_fb31728b.png',
    'down-alternative-pillow-1':                      CDN + '/fluffco-down-alternative-pillow-tan-tile_df979215.webp',
    'down-feather-pillow':                            CDN + '/fluffco-down-feather-pillow-tan-tile_fd0aba65.webp',
    '2x-hotel-pillows-pillowcase-set-down-alternative': CDN + '/2x_1_8bfa7624.jpg',
    '2x-hotel-pillows-pillowcase-set-down-feather-1':   CDN + '/2x_1_8bfa7624.jpg',
  };

  // ─── GWP milestones (added to cart silently at checkout only) ────────────────
  var GWP_MILESTONES = [
    {
      threshold: 10000,
      reward: 'Free Shipping',
      icon: '🚚',
      gwpKey: null,
      sizeVariants: null,
    },
    {
      threshold: 15000,
      reward: 'Free Pillowcase Set',
      icon: '🎁',
      gwpKey: 'pillowcase-set',
      gwpLabel: 'FREE GIFT 1',
      sizeVariants: [
        { size: 'Standard', variantId: 52100965990694 },
        { size: 'King',     variantId: 52100966023462 },
      ],
    },
    {
      threshold: 25000,
      reward: 'Free Linen Spray',
      icon: '🌿',
      gwpKey: 'linen-spray',
      gwpLabel: 'FREE GIFT 2',
      sizeVariants: [
        { size: 'Lavender', variantId: 49457232249126 },
        { size: 'Rose',     variantId: 49457232281894 },
      ],
    },
  ];

  var URGENCY_MINUTES = 5;

  // Memorial Day sale: 15% off the Memorial Day Sale collection. Shopify's
  // automatic discount applies at checkout but not always in cart.js, so we
  // mirror the rate here for the cart drawer math. Set to 0 when the sale ends.
  var MEMORIAL_DAY_RATE = 0; // Memorial Day sale ENDED — no sitewide % stacked on bundles

  // ─── State ───────────────────────────────────────────────────────────────────
  var cartData       = null; // cached cart — render immediately on open
  var isOpen         = false;
  var timerInterval  = null;
  var timerEnd       = null;
  var upsellIdx      = 0;
  var gwpSizes       = {};   // gwpKey → size string
  var expandedSlug   = null;
  var selectedVids   = {};   // upsell slug → variantId
  var upsellData     = [];
  var discountCode   = '';
  var showPromoInput = false;

  // ─── Discount pre-fetch state ─────────────────────────────────────────────────
  // Code is generated in the background as soon as bundle/savings items hit the
  // cart. At checkout we use it immediately — zero extra wait.
  var _prefetchedCode     = null;  // the generated discount code string
  var _prefetchedSig      = null;  // savingsCents string when code was generated
  var _prefetchInFlight   = false; // prevent concurrent Worker calls

  // ─── Currency helpers ─────────────────────────────────────────────────────────
  // window.Shopify.currency is injected by Shopify when multi-currency is active.
  // Shopify's /cart.js endpoint returns prices in the PRESENTMENT currency
  // (the user's active currency) cents already — we must NOT multiply by the
  // USD→presentment rate again or we'd double-convert (e.g. 4× $69 USD lands
  // as HK$9,843 instead of HK$2,196). Line item properties we stamp at ATC
  // (e.g. _target_total_cents from the PDP) are also already in presentment
  // cents because the PDP pre-converts SIZE_DATA by Shopify.currency.rate.
  var _currCode = 'USD';
  var _currRate = 1;  // kept for legacy callsites; fmt() ignores it now
  var _currLocale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en-US';
  (function () {
    var sc = window.Shopify && window.Shopify.currency;
    var fc = window.FluffCoCurrency;
    if (sc && sc.rate) { _currRate = parseFloat(sc.rate) || 1; }
    else if (fc && fc.rate) { _currRate = parseFloat(fc.rate) || 1; }
    if (sc && sc.active) { _currCode = sc.active; }
    else if (fc && fc.active) { _currCode = fc.active; }
  })();
  // JPY / KRW / VND etc. have zero fraction digits — Intl handles that automatically.
  var _currFmt;
  try {
    _currFmt = new Intl.NumberFormat(_currLocale, { style: 'currency', currency: _currCode });
  } catch (e) {
    // Fallback if currency code unsupported by the browser
    _currFmt = { format: function (n) { return _currCode + ' ' + n.toFixed(2); } };
  }
  // Whole-number formatter — used when the converted amount has no fractional part,
  // to match the FluffCo style of "$199" vs "$199.99" only when necessary.
  var _currFmtWhole;
  try {
    _currFmtWhole = new Intl.NumberFormat(_currLocale, { style: 'currency', currency: _currCode, minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } catch (e) {
    _currFmtWhole = _currFmt;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  function eid(id) { return document.getElementById(id); }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // fmt: input is already in the active PRESENTMENT currency cents (Shopify
  // cart.js line prices, ATC-stamped bundle targets which the PDP pre-converts).
  function fmt(cents) {
    var n = cents / 100;
    return (n === Math.floor(n) ? _currFmtWhole : _currFmt).format(n);
  }
  // fmtUsd: input is USD (base-currency) cents authored in Liquid — e.g. upsell
  // variant.price, which Liquid emits in the shop's base currency. Multiply by
  // the USD→presentment rate before formatting.
  function fmtUsd(usdCents) {
    var n = (usdCents * _currRate) / 100;
    return (n === Math.floor(n) ? _currFmtWhole : _currFmt).format(n);
  }

  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  // ─── Cart AJAX helpers ───────────────────────────────────────────────────────
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
  }

  function apiChange(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
  }

  // Strip GWP line items from Shopify cart. They're added right before
  // checkout via addGwpBeforeCheckout, but if the customer bails out of the
  // checkout flow they stick around. We clear them on init and on cart-open
  // so the customer only ever sees GWPs (and pays $0 for them) at checkout.
  function cleanupStaleGwps(cart) {
    if (!cart || !cart.items) return Promise.resolve(cart);
    var gwpKeys = [];
    cart.items.forEach(function (i) {
      if (i.properties && i.properties._gwp_key) gwpKeys.push(i.key);
    });
    if (!gwpKeys.length) return Promise.resolve(cart);
    var updates = {};
    gwpKeys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    }).catch(function () { return cart; });
  }

  function apiAdd(variantId, qty, props) {
    var body = { id: variantId, quantity: qty || 1 };
    if (props) body.properties = props;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
  }

  // Synthesize a cart-line-item from local upsell data so we can render
  // BEFORE the server confirms the add. Only fields used by buildItemsHtml /
  // displaySubtotal need to be present and correct.
  function buildOptimisticItem(upsell, variant) {
    var priceCents = Math.round((upsell.price || 0) * 100);
    return {
      key: 'pending-' + variant.id,
      variant_id: variant.id,
      product_title: upsell.name,
      variant_title: variant.title === 'Default Title' ? '' : variant.title,
      handle: upsell.slug,
      quantity: 1,
      price: priceCents,
      final_price: priceCents,
      original_line_price: priceCents,
      final_line_price: priceCents,
      image: upsell.image,
      featured_image: { url: upsell.image },
      properties: null,
      line_level_discount_allocations: [],
    };
  }

  // ─── Fast add: 1 round-trip instead of 2 ─────────────────────────────────────
  // /cart/add.js returns the line item that was added. We merge it into our local
  // cartData and skip the follow-up /cart.js round-trip. We also pre-render an
  // optimistic placeholder so the cart updates feel instant.
  function addItemFast(variantId, qty, props, optimisticItem) {
    qty = qty || 1;
    var addedKey = null;

    // 1) Optimistic state — if caller supplied a synthesized item, render it now
    if (optimisticItem && cartData) {
      var existing = cartData.items.find(function (i) {
        return i.variant_id === variantId;
      });
      var nextItems;
      if (existing) {
        nextItems = cartData.items.map(function (i) {
          if (i.variant_id !== variantId) return i;
          return Object.assign({}, i, {
            quantity: i.quantity + qty,
            final_line_price:    (i.final_price || i.price || 0) * (i.quantity + qty),
            original_line_price: (i.price || 0) * (i.quantity + qty),
          });
        });
      } else {
        nextItems = cartData.items.concat([optimisticItem]);
      }
      var optimisticCart = Object.assign({}, cartData, {
        items: nextItems,
        item_count: nextItems.reduce(function (sum, i) { return sum + i.quantity; }, 0),
      });
      render(optimisticCart);
    }

    // 2) Server confirm — single round-trip via /cart/add.js
    var reqId = ++_cartReqSeq;
    return apiAdd(variantId, qty, props).then(function (added) {
      addedKey = added && added.key;
      if (reqId !== _cartReqSeq) {
        return added;
      }
      if (!cartData) {
        // First-ever add (no cart loaded yet) — must fetch full state
        return fetchCart().then(function (c) { render(c); return added; });
      }
      // Merge the confirmed line into cartData
      var existing = cartData.items.find(function (i) { return i.key === addedKey; });
      var nextItems;
      if (existing) {
        nextItems = cartData.items.map(function (i) {
          return i.key === addedKey ? added : i;
        });
      } else {
        nextItems = cartData.items.concat([added]);
      }
      var confirmedCart = Object.assign({}, cartData, {
        items: nextItems,
        item_count: nextItems.reduce(function (sum, i) { return sum + i.quantity; }, 0),
      });
      // Only re-render if line count / qty differs from optimistic state
      if (!optimisticItem || !cartsLookSame(cartData, confirmedCart)) {
        render(confirmedCart);
      } else {
        cartData = confirmedCart;
        maybePrefetchCode(confirmedCart);
        updateHeaderCount(confirmedCart.item_count);
        renderMilestones(confirmedCart);
      }
      return added;
    }).catch(function (err) {
      // Roll back on error — re-fetch real state
      fetchCart().then(render);
      throw err;
    });
  }

  // ─── GWP checkout helper ──────────────────────────────────────────────────────
  // Called only from handleCheckout — adds unlocked GWP to the real Shopify cart
  // right before the redirect so they appear in Shopify checkout.
  function addGwpBeforeCheckout() {
    if (!cartData) return Promise.resolve();
    // GWP is US-only. International markets may not have the free-gift products
    // in their market catalog, and adding an out-of-catalog product makes
    // Shopify checkout reload in a loop. Skip GWP entirely outside the US/base
    // currency. (Also matches the business decision to remove GWP internationally.)
    var _shopCur = (window.FluffCoCurrency && window.FluffCoCurrency.shop) ||
                   (window.Shopify && window.Shopify.shopCurrency) || 'USD';
    if (_currCode && _currCode !== _shopCur) return Promise.resolve();
    var subtotal = effectiveSubtotalCents(cartData);
    var jobs = [];
    GWP_MILESTONES.forEach(function (m) {
      if (!m.gwpKey || !m.sizeVariants) return;
      if (subtotal < Math.round(m.threshold * _currRate)) return;
      var size    = gwpSizes[m.gwpKey] || m.sizeVariants[0].size;
      var variant = m.sizeVariants.find(function (v) { return v.size === size; }) || m.sizeVariants[0];
      // Only add if not already present (handles back-button → reopen → checkout)
      var exists = cartData.items.some(function (i) {
        return i.properties && i.properties._gwp_key === m.gwpKey;
      });
      if (!exists) {
        jobs.push(
          apiAdd(variant.variantId, 1, { _gwp_key: m.gwpKey, _gwp_size: size })
            .catch(function (e) { console.warn('[FC] GWP add failed', e); })
        );
      }
    });
    return Promise.all(jobs);
  }

  // ─── Subtotal: sum non-GWP item final_line_price directly ───────────────────
  // Never use cart.total_price — it can include automatic discounts, taxes, or
  // stale GWP line items that would make threshold math wrong.
  function displaySubtotal(cart) {
    return cart.items.reduce(function (sum, item) {
      if (item.properties && item.properties._gwp_key) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  // The amount the customer actually PAYS (bundle target + Memorial Day applied,
  // or MD on non-bundle lines) — i.e. the cart's real subtotal, not the
  // pre-discount line price. GWP / free-shipping thresholds must use THIS, or a
  // 4-pack that displays $155.55 would wrongly unlock the $250 tier off its
  // $276 full price.
  function effectiveSubtotalCents(cart) {
    var pct = bybEffectivePct(cart);
    var total = 0;
    cart.items.forEach(function (i) {
      if (!i.properties) { total += (i.final_line_price || 0); return; }
      if (i.properties._gwp_key) return;
      var fin  = i.final_line_price || 0;
      var orig = i.original_line_price || 0;
      var eff  = effectiveBundleTargetForItem(i, pct);
      var isByb = i.properties._bundle_source === 'byb';
      if (eff > 0) {
        var line = eff;
        if (!isByb && MEMORIAL_DAY_RATE > 0) {
          var observed = (orig > 0 && orig > fin) ? (orig - fin) / orig : 0;
          var mdRate = observed > 0 ? observed : MEMORIAL_DAY_RATE;
          line = Math.round(eff * (1 - mdRate));
        }
        total += line;
      } else if (MEMORIAL_DAY_RATE > 0 && orig > 0 && orig === fin) {
        total += Math.round(fin * (1 - MEMORIAL_DAY_RATE));
      } else {
        total += fin;
      }
    });
    return total;
  }

  // ─── Header cart count ────────────────────────────────────────────────────────
  function updateHeaderCount(count) {
    // Exclude GWP items from the visible count
    var displayCount = count;
    if (cartData) {
      var gwpCount = cartData.items.reduce(function (n, i) {
        return n + (i.properties && i.properties._gwp_key ? i.quantity : 0);
      }, 0);
      displayCount = Math.max(0, count - gwpCount);
    }
    document.querySelectorAll('.fc-cart-count').forEach(function (el) {
      el.textContent = displayCount;
      el.setAttribute('data-empty', displayCount === 0 ? 'true' : 'false');
    });
    var badge = eid('fc-cart-badge');
    if (!badge) return;
    if (displayCount > 0) {
      badge.textContent  = displayCount;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  }

  // ─── Timer ───────────────────────────────────────────────────────────────────
  function startTimer() {
    var stored = sessionStorage.getItem('fc_cart_timer_end');
    var now    = Date.now();
    if (stored && parseInt(stored, 10) > now) {
      timerEnd = parseInt(stored, 10);
    } else {
      timerEnd = now + URGENCY_MINUTES * 60 * 1000;
      sessionStorage.setItem('fc_cart_timer_end', String(timerEnd));
    }
    clearInterval(timerInterval);
    tickTimer();
    timerInterval = setInterval(tickTimer, 1000);
  }

  function tickTimer() {
    var el = eid('fc-cart-timer');
    if (!el) return;
    var rem = Math.max(0, Math.round((timerEnd - Date.now()) / 1000));
    el.textContent = pad2(Math.floor(rem / 60)) + ':' + pad2(rem % 60);
    if (rem === 0) clearInterval(timerInterval);
  }

  // ─── Milestone progress ───────────────────────────────────────────────────────
  // Builds structure once (stable IDs), then updates fill width in-place so the
  // CSS transition on the fill span actually has a before/after state to animate.
  // NOTE: bar elements are <span> not <div> — Dawn base.css has div:empty{display:none}
  // which kills any childless div. span:empty is NOT in that rule.
  function renderMilestones(cart) {
    var wrap  = eid('fc-cart-milestones');
    var urgEl = eid('fc-cart-urgency');
    if (!wrap) return;

    var realCount = cart.items.reduce(function (n, i) {
      return n + (i.properties && i.properties._gwp_key ? 0 : i.quantity);
    }, 0);

    if (realCount === 0) {
      wrap.style.display = 'none';
      if (urgEl) urgEl.style.display = 'none';
      return;
    }

    wrap.style.setProperty('display', 'block', 'important');
    if (urgEl) urgEl.style.setProperty('display', 'flex', 'important');
    startTimer();

    // Use the EFFECTIVE (post-discount) subtotal — what the customer pays — so
    // GWP tiers unlock on the real spend, not the pre-discount line price.
    var sub = effectiveSubtotalCents(cart);

    // GWP is US-only for the gift tiers (Pillowcase Set $150, Linen Spray $250).
    // Outside the US we only surface Free Shipping (threshold-only, no product
    // gift). Match the existing US gate used by addGwpBeforeCheckout so the
    // displayed milestones stay in sync with what actually unlocks at checkout.
    var _shopCurForMs = (window.FluffCoCurrency && window.FluffCoCurrency.shop) ||
                        (window.Shopify && window.Shopify.shopCurrency) || 'USD';
    var _isUSMarket   = !_currCode || _currCode === _shopCurForMs;
    var _active = _isUSMarket
      ? GWP_MILESTONES
      : GWP_MILESTONES.filter(function (m) { return !m.gwpKey; });

    // GWP thresholds are authored in USD cents. The cart subtotal `sub` is in
    // PRESENTMENT cents (HKD, EUR, etc). Convert each threshold to presentment
    // cents before comparing so the progress bar / unlocked state work for
    // every market.
    function thresholdInPresentment(usdCents) {
      return Math.round(usdCents * _currRate);
    }
    var next  = null;
    var lastU = null;
    for (var i = 0; i < _active.length; i++) {
      var t = thresholdInPresentment(_active[i].threshold);
      if (sub >= t) { lastU = _active[i]; }
      else if (!next) { next = _active[i]; }
    }
    var maxThreshPresent = thresholdInPresentment(_active[_active.length - 1].threshold);
    var allUnlocked = sub >= maxThreshPresent;
    var from  = lastU ? thresholdInPresentment(lastU.threshold) : 0;
    var to    = next  ? thresholdInPresentment(next.threshold)  : maxThreshPresent;
    var pct   = next  ? Math.min(100, ((sub - from) / (to - from)) * 100) : 100;
    var fillW = (pct < 1 && pct > 0) ? 1 : pct;

    var fillBg = allUnlocked ? '#3a7d44' : 'linear-gradient(to right,#434780,#e8637a)';

    // Dots HTML
    var dotsHtml = _active.map(function (m) {
      var tPres = thresholdInPresentment(m.threshold);
      var unlocked = sub >= tPres;
      return '<div class="fc-milestone-dot-wrap">' +
        '<div class="fc-milestone-dot' + (unlocked ? ' unlocked' : '') + '">' +
          (unlocked
            ? '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7.5L8 2.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<span class="fc-milestone-dot-icon">' + m.icon + '</span>') +
        '</div>' +
        '<span class="fc-milestone-dot-label' + (unlocked ? ' unlocked' : '') + '">' + fmt(tPres) + '</span>' +
      '</div>';
    }).join('');

    // Status text
    var statusText;
    if (allUnlocked) {
      statusText = '<span class="fc-text-green">🎉 Maximum rewards unlocked!</span>';
    } else if (next) {
      var nextPres = thresholdInPresentment(next.threshold);
      var remAmt   = Math.ceil((nextPres - sub) / 100);
      var remFmt   = _currFmtWhole.format(remAmt);
      statusText = 'Add <span>' + remFmt + ' more</span> to unlock ' + next.icon + ' ' + esc(next.reward);
    } else {
      statusText = '';
    }

    // ── If the bar structure already exists, update in-place (preserves transition) ──
    var fillEl   = document.getElementById('fc-bar-fill');
    var dotsEl   = document.getElementById('fc-bar-dots');
    var statusEl = document.getElementById('fc-bar-status');

    if (fillEl && dotsEl && statusEl) {
      // Animate the fill by just changing width — transition fires because element persists
      fillEl.style.width      = fillW.toFixed(1) + '%';
      fillEl.style.background = fillBg;
      dotsEl.innerHTML        = dotsHtml;
      statusEl.innerHTML      = statusText;
      statusEl.className      = 'fc-cart-milestone-text' + (allUnlocked ? ' unlocked' : '');
    } else {
      // First render — build the full structure with stable IDs
      wrap.innerHTML =
        '<div class="fc-milestone-dots" id="fc-bar-dots">' + dotsHtml + '</div>' +
        '<span style="display:block;height:6px;border-radius:9999px;background:rgba(67,71,128,0.18);margin-bottom:5px;position:relative;overflow:hidden;">' +
          '<span id="fc-bar-fill" style="display:block;position:absolute;top:0;left:0;height:6px;border-radius:9999px;background:' + fillBg + ';width:' + fillW.toFixed(1) + '%;transition:width 0.45s ease;"></span>' +
        '</span>' +
        '<p class="fc-cart-milestone-text' + (allUnlocked ? ' unlocked' : '') + '" id="fc-bar-status">' + statusText + '</p>';
    }
  }

  // ─── Cart item image (CDN-first, then Shopify fallback) ───────────────────────
  function itemImg(item) {
    var src = CDN_IMAGES[item.handle] || (item.featured_image && item.featured_image.url) || '';
    if (src) {
      return '<img class="fc-citem-img" src="' + esc(src) + '" alt="' + esc(item.product_title) + '" loading="lazy">';
    }
    return '<div class="fc-citem-img fc-citem-img--placeholder"></div>';
  }

  // ─── BYB live discount tier ──────────────────────────────────────────────────
  // BYB bundles are re-tiered live as the user adds/removes lines from the cart.
  // We can't mutate Shopify line item properties post-add, so the original
  // _bundle_pct stamp is ignored and pct is recomputed from current BYB qty:
  //   2 items → 10%, 3 items → 15%, 4+ items → 20%, otherwise no BYB discount.
  // Compute the effective bundle target (in cents) for a line, given the live
  // BYB tier pct. Returns 0 when no bundle discount applies.
  function effectiveBundleTargetForItem(item, bybPct) {
    if (!item.properties) return 0;
    if (item.properties._gwp_key) return 0;
    var target   = parseInt(item.properties._target_total_cents, 10);
    var bQty     = parseInt(item.properties._bundle_qty, 10);
    var uCents   = parseInt(item.properties._unit_price_cents, 10);
    var isByb    = item.properties._bundle_source === 'byb';
    if (isByb && uCents > 0) {
      if (bybPct > 0) return Math.round(uCents * item.quantity * (1 - bybPct / 100));
      return 0;
    }
    if (target > 0 && bQty > 0 && item.quantity !== bQty) {
      if (item.quantity > bQty && uCents > 0) return target + (item.quantity - bQty) * uCents;
      return 0;
    }
    return target > 0 ? target : 0;
  }

  function bybEffectivePct(cart) {
    var qty = 0;
    cart.items.forEach(function (i) {
      if (i.properties && i.properties._bundle_source === 'byb' && !i.properties._gwp_key) {
        qty += (i.quantity || 0);
      }
    });
    if (qty >= 4) return 20;
    if (qty === 3) return 15;
    if (qty === 2) return 10;
    return 0;
  }

  // ─── Cart items HTML ──────────────────────────────────────────────────────────
  function buildItemsHtml(cart) {
    // GWP items are completely hidden — they're added silently at checkout
    var items = cart.items.filter(function (i) {
      return !(i.properties && i.properties._gwp_key);
    });

    if (!items.length) return '';

    var bybPct = bybEffectivePct(cart);

    return items.map(function (item) {
      // Bundle items use the bundle target as the line price. Otherwise use
      // Shopify's final_line_price (already reflects any cart-level discount).
      var bundleTarget = item.properties && parseInt(item.properties._target_total_cents, 10);
      var bundleQty   = item.properties && parseInt(item.properties._bundle_qty, 10);
      var unitCents   = item.properties && parseInt(item.properties._unit_price_cents, 10);
      var isBybItem   = item.properties && item.properties._bundle_source === 'byb';
      var effectiveBundleTarget = bundleTarget;
      if (isBybItem && unitCents > 0) {
        // BYB: pct is live-recomputed from current cart contents (handles
        // remove/add inside the cart). If tier dropped to 0, no bundle discount.
        if (bybPct > 0) {
          effectiveBundleTarget = Math.round(unitCents * item.quantity * (1 - bybPct / 100));
        } else {
          effectiveBundleTarget = 0;
        }
      } else if (bundleTarget > 0 && bundleQty > 0 && item.quantity !== bundleQty) {
        // Pillow PDP bundle: target covers _bundle_qty units; extras at unit price.
        if (item.quantity > bundleQty && unitCents > 0) {
          effectiveBundleTarget = bundleTarget + (item.quantity - bundleQty) * unitCents;
        } else {
          effectiveBundleTarget = 0;
        }
      }
      var linePrice    = (effectiveBundleTarget > 0) ? effectiveBundleTarget : item.final_line_price;
      // BYB bundles override sitewide sales — no Memorial Day stack on these.
      var isByb = item.properties && item.properties._bundle_source === 'byb';
      // Pillow PDP bundle items: show the post-Memorial-Day price next to the
      // product so the line price matches the cart subtotal math.
      if (!isByb && effectiveBundleTarget > 0 && MEMORIAL_DAY_RATE > 0 && !(item.properties && item.properties._gwp_key)) {
        var _bOrig = item.original_line_price || 0;
        var _bFin  = item.final_line_price || 0;
        var _bObserved = (_bOrig > 0 && _bOrig > _bFin) ? (_bOrig - _bFin) / _bOrig : 0;
        var _bMdRate = _bObserved > 0 ? _bObserved : MEMORIAL_DAY_RATE;
        linePrice = Math.round(effectiveBundleTarget * (1 - _bMdRate));
      }
      // Memorial Day fallback for non-bundle items: when Shopify's automatic
      // MD discount hasn't reflected on the line yet (final == original), apply
      // it client-side so the cart still shows the sale price.
      var mdAppliedNonBundle = false;
      if (!(bundleTarget > 0) && MEMORIAL_DAY_RATE > 0 && !(item.properties && item.properties._gwp_key)) {
        var _orig = item.original_line_price || 0;
        var _fin  = item.final_line_price || 0;
        if (_orig > 0 && _orig === _fin) {
          linePrice = Math.round(_fin * (1 - MEMORIAL_DAY_RATE));
          mdAppliedNonBundle = true;
        }
      }
      // Vanity strikethrough comes from the property we stamped at ATC; falls
      // back to Shopify's original_line_price (variant compare_at × qty) if absent.
      var compareTotal = item.properties && parseInt(item.properties._compare_total_cents, 10);
      var origPrice    = (compareTotal > 0) ? compareTotal : item.original_line_price;
      var hasDiscount  = origPrice > linePrice;
      var varTitle      = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';

      // Discount badge logic — prefer the Shopify-applied discount label
      // (Memorial Day Sale etc.) since the bundle price is already baked into
      // the line price shown above. Fall back to "BUNDLE DISCOUNT" only if no
      // Shopify discount is attached.
      var hasBundle     = item.properties && item.properties._bundle_pct;
      var discountLabel = null;
      // BYB lines: force our own label — never surface unrelated automatic
      // discount titles (e.g. a "GWP – FREE ROBE" automatic discount Shopify
      // happened to attach to the same product).
      if (isByb) {
        discountLabel = bybPct > 0 ? ('BUNDLE DISCOUNT • ' + bybPct + '% OFF') : null;
      } else if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
        var alloc = item.line_level_discount_allocations[0];
        if (alloc && alloc.discount_application && alloc.discount_application.title) {
          var allocTitle = alloc.discount_application.title;
          // Filter out GWP / free-product discount titles from the line badge —
          // those are silent at the line level; GWPs are handled at checkout.
          if (!/gwp|free\s*robe|free\s*pillowcase|free\s*linen/i.test(allocTitle)) {
            discountLabel = allocTitle;
          }
        }
      }
      if (!discountLabel && hasBundle && !isByb) {
        // Pillow PDP bundles get the MD label when the sale is active because
        // MD is being applied on top of the bundle target.
        discountLabel = MEMORIAL_DAY_RATE > 0 ? 'MEMORIAL DAY SALE' : 'BUNDLE DISCOUNT';
      }
      if (!discountLabel && mdAppliedNonBundle) {
        discountLabel = 'MEMORIAL DAY SALE';
      }
      var discountBadge = discountLabel
        ? '<div class="fc-citem-discount-badge"><span class="fc-citem-discount-tag">🏷</span> <strong>' + esc(discountLabel) + '</strong> <span class="fc-citem-discount-applied">applied</span></div>'
        : '';

      return '<div class="fc-citem" data-key="' + esc(item.key) + '">' +
        itemImg(item) +
        '<div class="fc-citem-info">' +
          '<p class="fc-citem-name">' + esc(item.product_title) + '</p>' +
          (varTitle ? '<p class="fc-citem-variant">' + esc(varTitle) + '</p>' : '') +
          '<div class="fc-citem-price-row">' +
            '<span class="fc-citem-price">' + fmt(linePrice) + '</span>' +
            (hasDiscount ? '<span class="fc-citem-compare">' + fmt(origPrice) + '</span>' : '') +
          '</div>' +
          discountBadge +
          '<div class="fc-citem-actions">' +
            '<div class="fc-qty-stepper">' +
              '<button class="fc-qty-btn" data-action="dec" data-key="' + esc(item.key) + '" data-qty="' + item.quantity + '" aria-label="Decrease quantity">−</button>' +
              '<span class="fc-qty-value">' + item.quantity + '</span>' +
              '<button class="fc-qty-btn" data-action="inc" data-key="' + esc(item.key) + '" data-qty="' + item.quantity + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button class="fc-remove-btn" data-key="' + esc(item.key) + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    }).join('');
  }

  // ─── Upsell HTML ─────────────────────────────────────────────────────────────
  function buildUpsellHtml(cart) {
    if (!upsellData.length) return '';
    var cartHandles = cart.items.map(function (i) { return i.handle; });
    var visible = upsellData.filter(function (u) { return cartHandles.indexOf(u.slug) === -1; });
    if (!visible.length) return '';

    var idx = ((upsellIdx % visible.length) + visible.length) % visible.length;
    var u   = visible[idx];

    var isDefaultTitle = u.variants.length === 1 && (u.variants[0].title === 'Default Title' || u.variants[0].title === 'Default');
    var showPicker     = u.variants.length > 1 && !isDefaultTitle;
    var expanded       = expandedSlug === u.slug;
    var hasSelection   = !!selectedVids[u.slug];

    var variantBtnsHtml = showPicker ? u.variants.map(function (v) {
      var sel = selectedVids[u.slug] === v.id;
      return '<button class="fc-upsell-variant-btn' + (sel ? ' selected' : '') + '" data-slug="' + esc(u.slug) + '" data-vid="' + v.id + '">' + esc(v.title) + '</button>';
    }).join('') : '';

    return '<div class="fc-cart-upsells">' +
      '<div class="fc-upsells-header">' +
        '<span class="fc-upsells-label">Complete your setup</span>' +
        (visible.length > 1 ? '<div class="fc-upsell-nav">' +
          '<button class="fc-upsell-nav-btn" data-dir="-1" aria-label="Previous">‹</button>' +
          '<button class="fc-upsell-nav-btn" data-dir="1" aria-label="Next">›</button>' +
        '</div>' : '') +
      '</div>' +
      '<div class="fc-upsell-card">' +
        '<div class="fc-upsell-top">' +
          '<img class="fc-upsell-img" src="' + esc(u.image) + '" alt="' + esc(u.name) + '" loading="lazy">' +
          '<div class="fc-upsell-info">' +
            '<p class="fc-upsell-name">' + esc(u.name) + '</p>' +
            '<p class="fc-upsell-sub">' + esc(u.subtitle) + '</p>' +
            '<div class="fc-upsell-price-row">' +
              '<span class="fc-upsell-price">' + fmtUsd(u.price) + '</span>' +
              (u.comparePrice ? '<span class="fc-upsell-compare">' + fmtUsd(u.comparePrice) + '</span>' : '') +
            '</div>' +
          '</div>' +
          '<button class="fc-upsell-add-btn" data-slug="' + esc(u.slug) + '" data-single="' + (!showPicker ? '1' : '0') + '">' +
            (expanded ? '✕' : '+ Add') +
          '</button>' +
        '</div>' +
        (showPicker ?
          '<div class="fc-upsell-variants' + (expanded ? ' expanded' : '') + '">' +
            '<p class="fc-upsell-variant-label">Select an option</p>' +
            '<div class="fc-upsell-variant-btns">' + variantBtnsHtml + '</div>' +
            '<button class="fc-upsell-confirm-btn' + (hasSelection ? ' ready' : '') + '" data-slug="' + esc(u.slug) + '"' + (hasSelection ? '' : ' disabled') + '>Add to Cart →</button>' +
          '</div>'
        : '') +
      '</div>' +
    '</div>';
  }

  // ─── Footer HTML ─────────────────────────────────────────────────────────────
  function buildFooterHtml(cart) {
    var sub          = displaySubtotal(cart);           // pre-discount (for strikethrough original)
    var effSub       = effectiveSubtotalCents(cart);    // post-discount (for GWP tier unlocks)
    var hasDiscount  = cart.total_discount > 0;
    // Bundle savings = (current line price after any sitewide %) − bundle target.
    // The PDP stamps _target_total_cents per line item; we compute the delta
    // dynamically so the customer always lands on the bundle price exactly,
    // even when a sitewide % discount has already shrunk the line.
    var bundleItem   = cart.items.find(function(i) { return i.properties && i.properties._bundle_pct; });
    var bundlePct    = bundleItem ? parseInt(bundleItem.properties._bundle_pct, 10) : 0;
    // Compute three deductions per line:
    //   1) bundleDelta — what we must take off the variant line to land on the
    //      bundle target ($276 line - $183 target = $93 raw, or $234.60 - $183
    //      = $51.60 when MD has already shrunk the line).
    //   2) mdOnBundle   — Memorial Day 15% off the bundle target itself, on
    //      top of the bundle deal. The user wants the bundle price displayed
    //      cleanly and MD shown as a SEPARATE line.
    // For non-bundle lines we trust Shopify's MD math (it already shrank the
    // line price).
    var bundleSave    = 0;  // raw delta from line price to bundle target
    var bundleSubtotal = 0; // sum of bundle targets — drives the "Subtotal" display
    var bundleMdSave  = 0;  // MD 15% off bundle target
    var nonBundleMdSave = 0; // MD fallback for 1x lines Shopify hasn't discounted
    var footerBybPct = bybEffectivePct(cart);
    cart.items.forEach(function (i) {
      if (!i.properties) return;
      if (i.properties._gwp_key) return;
      var orig = i.original_line_price || 0;
      var fin  = i.final_line_price || 0;
      var effTarget = effectiveBundleTargetForItem(i, footerBybPct);
      var isBybLine = i.properties._bundle_source === 'byb';
      if (effTarget > 0 && fin > effTarget) {
        bundleSave    += fin - effTarget;
        bundleSubtotal += effTarget;
        // BYB bundle overrides sitewide sales — no MD on these lines.
        if (!isBybLine) {
          var observed = (orig > 0 && orig > fin) ? (orig - fin) / orig : 0;
          var mdRate = observed > 0 ? observed : MEMORIAL_DAY_RATE;
          if (mdRate > 0) bundleMdSave += Math.round(effTarget * mdRate);
        }
      } else if (!(effTarget > 0) && MEMORIAL_DAY_RATE > 0 && orig > 0 && orig === fin) {
        nonBundleMdSave += Math.round(fin * MEMORIAL_DAY_RATE);
      }
    });
    // Cart subtotal = bundle targets (clean) + everything else's final_line_price
    var nonBundleSub = 0;
    cart.items.forEach(function (i) {
      if (i.properties && i.properties._gwp_key) return;
      var eff = effectiveBundleTargetForItem(i, footerBybPct);
      if (!(eff > 0 && (i.final_line_price || 0) > eff)) {
        nonBundleSub += (i.final_line_price || 0);
      }
    });
    var subtotalDisplay = bundleSubtotal + nonBundleSub;
    var finalTotal      = subtotalDisplay - bundleMdSave - nonBundleMdSave;
    var shippingFree    = finalTotal >= Math.round(10000 * _currRate);
    // Workers/server-side discount math still uses the legacy combined number
    // so the checkout total matches what we show.
    var checkoutDiscount = bundleSave + bundleMdSave + nonBundleMdSave;

    // GWP pickers for unlocked milestones
    var gwpHtml = GWP_MILESTONES.filter(function (m) {
      return m.gwpKey && m.sizeVariants && effSub >= Math.round(m.threshold * _currRate);
    }).map(function (m) {
      var sel = gwpSizes[m.gwpKey] || m.sizeVariants[0].size;
      return '<div class="fc-gwp-picker">' +
        '<span class="fc-gwp-picker-label">' + m.icon + ' ' + esc(m.reward) + '</span>' +
        '<div class="fc-gwp-picker-btns">' +
          m.sizeVariants.map(function (v) {
            return '<button class="fc-gwp-size-btn' + (sel === v.size ? ' selected' : '') + '" data-gwp="' + esc(m.gwpKey) + '" data-size="' + esc(v.size) + '">' + esc(v.size) + '</button>';
          }).join('') +
        '</div>' +
      '</div>';
    }).join('');

    // Promo section
    var promoHtml;
    if (!showPromoInput) {
      promoHtml = '<button class="fc-promo-toggle" id="fc-promo-toggle">Have a promo code?</button>';
    } else {
      promoHtml = '<div class="fc-promo-input-row">' +
        '<input type="text" class="fc-promo-input" id="fc-promo-input" value="' + esc(discountCode) + '" placeholder="Enter promo code" autocapitalize="characters" autocomplete="off">' +
        '<button class="fc-promo-close" id="fc-promo-close">✕</button>' +
      '</div>' +
        (discountCode.trim() ? '<p class="fc-promo-confirm">✓ Code <strong>' + esc(discountCode.trim().toUpperCase()) + '</strong> will be applied at checkout</p>' : '');
    }

    // Vanity savings: sum up each line's (compare_total − effective price). This
    // matches the "Instant savings: $X" numbers the customer saw on the PDP
    // bundle picker, so the cart reinforces the marketing message instead of
    // showing some other (smaller) number. Falls back to Shopify's discount
    // accounting when no vanity compare was stamped on the line.
    var vanitySaved = 0;
    var vanityOriginal = 0;
    var anyVanity = false;
    cart.items.forEach(function (i) {
      if (i.properties && i.properties._gwp_key) return;
      var target  = parseInt(i.properties && i.properties._target_total_cents, 10);
      var compare = parseInt(i.properties && i.properties._compare_total_cents, 10);
      var effective = (target > 0) ? target : (i.final_line_price || 0);
      if (compare > 0) {
        anyVanity = true;
        vanityOriginal += compare;
        vanitySaved    += Math.max(0, compare - effective);
      } else {
        vanityOriginal += (i.original_line_price || i.final_line_price || 0);
      }
    });
    var totalSaved, originalTotal;
    if (anyVanity) {
      totalSaved    = vanitySaved + (hasDiscount ? cart.total_discount : 0);
      originalTotal = vanityOriginal;
    } else {
      totalSaved    = (hasDiscount ? cart.total_discount : 0) + bundleSave;
      originalTotal = (hasDiscount ? cart.original_total_price : sub) + bundleSave;
    }
    var showSavings  = totalSaved > 0;
    return (showSavings ?
      '<div class="fc-savings-row">' +
        '<span class="fc-savings-original">' + fmt(originalTotal) + '</span>' +
        '<span class="fc-savings-badge">🌸 ' + fmt(totalSaved) + ' saved</span>' +
      '</div>' : '') +
      '<div class="fc-subtotal-row">' +
        '<span class="fc-subtotal-label">Subtotal</span>' +
        '<span class="fc-subtotal-value">' + fmt(finalTotal) + '</span>' +
      '</div>' +
      '<p class="fc-shipping-note">' + (shippingFree ? '✓ Free shipping applied' : 'Shipping calculated at checkout') + '</p>' +
      '<div class="fc-est-shipping">' +
        '<span class="fc-est-shipping-icon">📦</span>' +
        '<p class="fc-est-shipping-text">Estimated delivery: <strong>3–5 business days</strong></p>' +
      '</div>' +
      gwpHtml +
      promoHtml +
      '<button class="fc-checkout-btn" id="fc-checkout-btn">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>' +
        'Secure Checkout · ' + fmt(finalTotal) +
      '</button>' +
      '<div class="fc-trust-row">' +
        '<span class="fc-trust-item"><span class="fc-trust-icon">🔒</span> SSL Secure</span>' +
        '<span class="fc-trust-item"><span class="fc-trust-icon">↩</span> Free Returns</span>' +
        '<span class="fc-trust-item"><span class="fc-trust-icon">⭐</span> 4.8 / 5 Stars</span>' +
      '</div>';
  }

  // ─── Full render ─────────────────────────────────────────────────────────────
  function render(cart) {
    cartData = cart;
    maybePrefetchCode(cart);

    var bodyEl   = eid('fc-cart-body');
    var footerEl = eid('fc-cart-footer');

    // Determine if cart has any real (non-GWP) items
    var realItems = cart.items.filter(function (i) {
      return !(i.properties && i.properties._gwp_key);
    });

    if (bodyEl) {
      if (realItems.length === 0) {
        bodyEl.innerHTML =
          '<div class="fc-cart-empty">' +
            '<div class="fc-cart-empty-icon">🛏️</div>' +
            '<h3 class="fc-cart-empty-title">Your cart is empty</h3>' +
            '<p class="fc-cart-empty-sub">Discover hotel-grade comfort — from pillows to robes — at a fraction of the price.</p>' +
            '<a href="/collections/all-products" class="fc-cart-empty-cta">Shop All Products</a>' +
          '</div>';
      } else {
        bodyEl.innerHTML =
          '<div class="fc-cart-items">' +
            buildItemsHtml(cart) +
            buildUpsellHtml(cart) +
          '</div>';
        attachBodyListeners(bodyEl);
      }
    }

    if (footerEl) {
      if (realItems.length === 0) {
        footerEl.style.display = 'none';
      } else {
        footerEl.innerHTML     = buildFooterHtml(cart);
        footerEl.style.display = '';
        attachFooterListeners(footerEl);
      }
    }

    renderMilestones(cart);
    updateHeaderCount(cart.item_count);
  }

  // ─── Optimistic qty update (renders before network confirms) ─────────────────
  // We mutate only the items array — displaySubtotal() re-derives the total
  // from item.final_line_price, so we never need to keep cart.total_price in sync.
  function optimisticQtyChange(key, newQty) {
    if (!cartData) return;
    var item = cartData.items.find(function (i) { return i.key === key; });
    if (!item) return;

    var fakeItems;
    var countDelta;

    if (newQty === 0) {
      fakeItems  = cartData.items.filter(function (i) { return i.key !== key; });
      countDelta = -item.quantity;
    } else {
      var diff = newQty - item.quantity;
      countDelta = diff;
      fakeItems = cartData.items.map(function (i) {
        if (i.key !== key) return i;
        return Object.assign({}, i, {
          quantity: newQty,
          final_line_price:    (i.final_price || 0) * newQty,
          original_line_price: (i.price       || 0) * newQty,
        });
      });
    }

    var fakeCart = Object.assign({}, cartData, {
      items:      fakeItems,
      item_count: Math.max(0, cartData.item_count + countDelta),
      // total_price kept as-is; displaySubtotal() reads item prices directly
    });
    // Commit optimistic state to cartData so a fast follow-up click (e.g.
    // double-tap remove) doesn't read stale items still in cartData.
    cartData = fakeCart;
    render(fakeCart);
  }

  // ─── Cart change request coordinator ─────────────────────────────────────────
  // Avoids the visible double-render flash by:
  //   1) tracking a sequence so stale server responses are dropped
  //   2) skipping the post-server re-render when the result matches our optimistic state
  var _cartReqSeq = 0;
  function cartsLookSame(a, b) {
    if (!a || !b) return false;
    if (a.item_count !== b.item_count) return false;
    if (a.items.length !== b.items.length) return false;
    for (var i = 0; i < a.items.length; i++) {
      if (a.items[i].key !== b.items[i].key) return false;
      if (a.items[i].quantity !== b.items[i].quantity) return false;
    }
    return true;
  }
  function changeQty(key, newQty) {
    optimisticQtyChange(key, newQty);
    var reqId = ++_cartReqSeq;
    _changeInFlight++;
    apiChange(key, newQty).then(function (confirmed) {
      _changeInFlight = Math.max(0, _changeInFlight - 1);
      if (reqId !== _cartReqSeq) {
        // A newer request is in flight — adopt this state silently
        cartData = confirmed;
        return;
      }
      if (cartsLookSame(cartData, confirmed)) {
        // Optimistic state already matches; just sync source of truth and skip re-render
        cartData = confirmed;
        maybePrefetchCode(confirmed);
        updateHeaderCount(confirmed.item_count);
        renderMilestones(confirmed);
      } else {
        render(confirmed);
      }
    }).catch(function () {
      _changeInFlight = Math.max(0, _changeInFlight - 1);
      fetchCart().then(render);
    });
  }
  // Count of in-flight /cart/change calls. Background cart-sync listeners
  // (Dawn pubsub, cart:refresh) skip their fetch when this is > 0 so they
  // can't reintroduce just-removed items by racing the change response.
  var _changeInFlight = 0;

  // ─── Listeners: body ─────────────────────────────────────────────────────────
  function attachBodyListeners(bodyEl) {
    bodyEl.querySelectorAll('.fc-qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key    = this.dataset.key;
        var action = this.dataset.action;
        var qty    = parseInt(this.dataset.qty, 10);
        var newQty = action === 'inc' ? qty + 1 : Math.max(0, qty - 1);
        changeQty(key, newQty);
      });
    });

    bodyEl.querySelectorAll('.fc-remove-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        changeQty(this.dataset.key, 0);
      });
    });

    // Upsell nav
    bodyEl.querySelectorAll('.fc-upsell-nav-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dir = parseInt(this.dataset.dir, 10);
        var cartHandles = cartData ? cartData.items.map(function (i) { return i.handle; }) : [];
        var visible = upsellData.filter(function (u) { return cartHandles.indexOf(u.slug) === -1; });
        if (!visible.length) return;
        upsellIdx = ((upsellIdx + dir) % visible.length + visible.length) % visible.length;
        expandedSlug = null;
        render(cartData);
      });
    });

    // Upsell add
    bodyEl.querySelectorAll('.fc-upsell-add-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var slug     = this.dataset.slug;
        var isSingle = this.dataset.single === '1';
        var u        = upsellData.find(function (x) { return x.slug === slug; });
        if (!u) return;

        if (expandedSlug === slug) {
          expandedSlug = null;
          render(cartData);
          return;
        }
        if (isSingle) {
          var vid = u.variants.length ? u.variants[0].id : null;
          if (!vid) return;
          var btn2 = this;
          btn2.textContent = '✓ Added';
          btn2.classList.add('added');
          btn2.disabled = true;
          var optItem = buildOptimisticItem(u, u.variants[0]);
          expandedSlug = null;
          addItemFast(vid, 1, null, optItem).catch(function () {});
        } else {
          expandedSlug = slug;
          render(cartData);
        }
      });
    });

    // Variant btn
    bodyEl.querySelectorAll('.fc-upsell-variant-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var slug = this.dataset.slug;
        var vid  = parseInt(this.dataset.vid, 10);
        selectedVids[slug] = vid;
        bodyEl.querySelectorAll('.fc-upsell-variant-btn[data-slug="' + slug + '"]').forEach(function (b) {
          b.classList.toggle('selected', parseInt(b.dataset.vid, 10) === vid);
        });
        var confirmBtn = bodyEl.querySelector('.fc-upsell-confirm-btn[data-slug="' + slug + '"]');
        if (confirmBtn) { confirmBtn.classList.add('ready'); confirmBtn.disabled = false; }
      });
    });

    // Variant confirm
    bodyEl.querySelectorAll('.fc-upsell-confirm-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var slug = this.dataset.slug;
        var vid  = selectedVids[slug];
        if (!vid) return;
        var u    = upsellData.find(function (x) { return x.slug === slug; });
        var v    = u && u.variants.find(function (vv) { return vv.id === vid; });
        var optItem = (u && v) ? buildOptimisticItem(u, v) : null;
        expandedSlug = null;
        delete selectedVids[slug];
        addItemFast(vid, 1, null, optItem).catch(function () {});
      });
    });
  }

  // ─── Listeners: footer ───────────────────────────────────────────────────────
  function attachFooterListeners(footerEl) {
    footerEl.querySelectorAll('.fc-gwp-size-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        gwpSizes[this.dataset.gwp] = this.dataset.size;
        // Update selection state without full re-render
        footerEl.querySelectorAll('.fc-gwp-size-btn[data-gwp="' + this.dataset.gwp + '"]').forEach(function (b) {
          b.classList.toggle('selected', b.dataset.size === btn.dataset.size);
        });
      });
    });

    var promoToggle = eid('fc-promo-toggle');
    if (promoToggle) {
      promoToggle.addEventListener('click', function () {
        showPromoInput = true;
        footerEl.innerHTML = buildFooterHtml(cartData);
        attachFooterListeners(footerEl);
        var inp = eid('fc-promo-input');
        if (inp) inp.focus();
      });
    }

    var promoClose = eid('fc-promo-close');
    if (promoClose) {
      promoClose.addEventListener('click', function () {
        showPromoInput = false;
        discountCode   = '';
        footerEl.innerHTML = buildFooterHtml(cartData);
        attachFooterListeners(footerEl);
      });
    }

    var promoInput = eid('fc-promo-input');
    if (promoInput) {
      promoInput.addEventListener('input', function () {
        discountCode = this.value;
        var confirmEl = footerEl.querySelector('.fc-promo-confirm');
        if (discountCode.trim()) {
          var html = '<p class="fc-promo-confirm">✓ Code <strong>' + esc(discountCode.trim().toUpperCase()) + '</strong> will be applied at checkout</p>';
          if (confirmEl) { confirmEl.outerHTML = html; }
          else {
            var row = footerEl.querySelector('.fc-promo-input-row');
            if (row) row.insertAdjacentHTML('afterend', html);
          }
        } else if (confirmEl) {
          confirmEl.remove();
        }
      });
      promoInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') handleCheckout();
      });
    }

    var checkoutBtn = eid('fc-checkout-btn');
    if (checkoutBtn) checkoutBtn.addEventListener('click', handleCheckout);
  }

  // ─── Checkout / discount pre-fetch ───────────────────────────────────────────
  // Cloudflare Worker URL — generates exact Shopify discount codes on the fly
  var DISCOUNT_WORKER_URL = 'https://fluffco-discount.sam-c25.workers.dev/';

  // MUST mirror the bundleSave math in buildFooterHtml so the Worker discount
  // code amount matches what the cart drawer shows. We prefer the dynamic
  // (current line price − bundle target) delta when _target_total_cents is
  // present, otherwise fall back to the static _savings_cents stamp.
  // Worker discount code amount must equal (bundle gap to target) + (15% off
  // the bundle target if Memorial Day is shrinking the line). This way the
  // Shopify checkout total matches the price the customer sees in the cart.
  function calcSavingsCents(cart) {
    var total = 0;
    var pct = bybEffectivePct(cart);
    cart.items.forEach(function (item) {
      if (!item.properties) return;
      if (item.properties._gwp_key) return;
      var fin    = item.final_line_price || 0;
      var orig   = item.original_line_price || 0;
      var eff    = effectiveBundleTargetForItem(item, pct);
      var bybLine = item.properties._bundle_source === 'byb';
      if (eff > 0 && fin > eff) {
        total += fin - eff;
        if (!bybLine) {
          var observed = (orig > 0 && orig > fin) ? (orig - fin) / orig : 0;
          var mdRate = observed > 0 ? observed : MEMORIAL_DAY_RATE;
          if (mdRate > 0) total += Math.round(eff * mdRate);
        }
      } else if (!(eff > 0) && MEMORIAL_DAY_RATE > 0 && orig > 0 && orig === fin) {
        total += Math.round(fin * MEMORIAL_DAY_RATE);
      }
    });
    // `total` is the savings in the active PRESENTMENT currency cents.
    return total;
  }

  // Discount as a fraction of the order subtotal (0–1). The Worker mints a
  // PERCENTAGE code from this — percentages are currency-agnostic, so they apply
  // identically in every market (USD/HKD/EUR…) with no amount conversion and no
  // checkout reload loop. pct × subtotal == savings, so the order total matches
  // the fixed-amount math exactly, in any currency.
  function calcDiscountPct(cart) {
    var savings = calcSavingsCents(cart);          // presentment cents
    if (savings <= 0) return 0;
    var subtotal = 0;
    cart.items.forEach(function (i) {
      if (i.properties && i.properties._gwp_key) return;
      subtotal += (i.final_line_price || 0);       // presentment cents
    });
    if (subtotal <= 0) return 0;
    // 4 dp matches Shopify's percentage precision; cap at 95%.
    return Math.min(0.95, Math.round((savings / subtotal) * 10000) / 10000);
  }

  // Called after every cart render. Fires the Worker in the background when the
  // savings amount changes, so the code is ready before the customer clicks checkout.
  function maybePrefetchCode(cart) {
    if (DISCOUNT_WORKER_URL === 'WORKER_URL_HERE') return;
    var pct = calcDiscountPct(cart);
    var sig = pct.toFixed(4);

    // Nothing to discount or percentage unchanged — keep existing cached code
    if (pct <= 0) { _prefetchedCode = null; _prefetchedSig = null; return; }
    if (sig === _prefetchedSig) return;
    if (_prefetchInFlight) return;

    // Invalidate stale code immediately so handleCheckout doesn't use a wrong code
    _prefetchedCode   = null;
    _prefetchedSig    = null;
    _prefetchInFlight = true;

    fetch(DISCOUNT_WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ percentage: pct }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        _prefetchInFlight = false;
        if (data.code) {
          _prefetchedCode = data.code;
          _prefetchedSig  = sig;
        }
      })
      .catch(function () {
        _prefetchInFlight = false;
      });
  }

  function handleCheckout() {
    var btn = eid('fc-checkout-btn');
    if (btn) {
      btn.disabled = true;
      btn.innerHTML =
        '<svg class="fc-spinner" width="14" height="14" viewBox="0 0 14 14" fill="none">' +
          '<circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="2" stroke-dasharray="20" stroke-dashoffset="10" stroke-linecap="round"/>' +
        '</svg> Processing…';
    }

    var manualCode = discountCode.trim();
    var pct        = cartData ? calcDiscountPct(cartData) : 0;
    var sig        = pct.toFixed(4);

    function doCheckout(code) {
      addGwpBeforeCheckout().then(function () {
        var url = '/checkout';
        if (code) url += '?discount=' + encodeURIComponent(code.toUpperCase());
        window.location.href = url;
      }).catch(function () {
        var url = '/checkout';
        if (code) url += '?discount=' + encodeURIComponent(code.toUpperCase());
        window.location.href = url;
      });
    }

    // Manual promo code always takes priority
    if (manualCode) { doCheckout(manualCode); return; }

    // No discount — straight to checkout. Percentage codes are currency-agnostic
    // so this path now works in every market (no per-currency gate needed).
    if (pct <= 0 || DISCOUNT_WORKER_URL === 'WORKER_URL_HERE') { doCheckout(''); return; }

    // Pre-fetched code is ready and still valid for the current cart — instant redirect
    if (_prefetchedCode && _prefetchedSig === sig) { doCheckout(_prefetchedCode); return; }

    // Pre-fetch is still in-flight — wait for it (max 4s then fall through without code)
    if (_prefetchInFlight) {
      var waited = 0;
      var poll = setInterval(function () {
        waited += 100;
        if (_prefetchedCode && _prefetchedSig === sig) {
          clearInterval(poll);
          doCheckout(_prefetchedCode);
        } else if (!_prefetchInFlight || waited >= 4000) {
          clearInterval(poll);
          doCheckout('');
        }
      }, 100);
      return;
    }

    // Prefetch missed entirely (e.g. Worker URL just set) — call now with spinner showing
    fetch(DISCOUNT_WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ percentage: pct }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { doCheckout(data.code || ''); })
      .catch(function () { doCheckout(''); });
  }

  // ─── Open ─────────────────────────────────────────────────────────────────────
  // Pass a pre-fetched cart to skip a redundant network call (e.g. after ATC).
  function open(preloadedCart) {
    var overlay = eid('fc-cart-overlay');
    var drawer  = eid('fc-cart-drawer');
    if (!overlay || !drawer) return;

    if (!isOpen) {
      isOpen = true;
      document.body.style.overflow = 'hidden';
      overlay.classList.add('open');
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
    }

    if (preloadedCart) {
      // Caller already has fresh data — render immediately, no fetch needed
      render(preloadedCart);
      // Still scrub stale GWPs in background — they shouldn't display anyway,
      // but we want them gone from Shopify so /cart page is clean too.
      cleanupStaleGwps(preloadedCart).then(function (clean) {
        if (clean !== preloadedCart) render(clean);
      });
    } else {
      // Render cached state instantly while fresh fetch runs in background
      if (cartData) render(cartData);
      fetchCart()
        .then(cleanupStaleGwps)
        .then(function (fresh) { render(fresh); })
        .catch(function (e) { console.error('[FC Cart] fetch error', e); });
    }
  }

  // ─── Close ───────────────────────────────────────────────────────────────────
  function close() {
    if (!isOpen) return;
    isOpen = false;

    var overlay = eid('fc-cart-overlay');
    var drawer  = eid('fc-cart-drawer');
    if (!overlay || !drawer) return;

    document.body.style.overflow = '';
    overlay.classList.remove('open');
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    clearInterval(timerInterval);
  }

  // ─── Auto-open on add-to-cart (fetch intercept) ──────────────────────────────
  // Wraps window.fetch to detect any successful /cart/add.js call from any source
  // (PDP form, upsells, Buy-it-now, etc.) and open the drawer automatically.
  function hookFetchForATC() {
    var _fetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url  = args[0];
      var urlStr = url ? (typeof url === 'string' ? url : (url.url || '')) : '';
      var result = _fetch.apply(window, args);

      if (urlStr.indexOf('/cart/add') !== -1) {
        // Open immediately — show cached state while ATC request is still in-flight
        if (!isOpen) open();

        // Stamp this add with the current request sequence. If the user does
        // anything else (qty change, remove) before this response lands, the
        // sequence will have advanced and we'll skip the stale merge — which
        // was making removed items briefly reappear.
        var addReqId = ++_cartReqSeq;

        // Try to optimistically bump an existing line's quantity so the cart
        // visually reflects the add instantly. Only safe when the variant is
        // already in the cart (we know the full line shape); for new variants
        // we wait for the server response (no placeholder).
        try {
          var addBody = args[1] && args[1].body;
          var vidGuess = null;
          var qtyGuess = 1;
          if (addBody instanceof FormData) {
            vidGuess = parseInt(addBody.get('id'), 10);
            qtyGuess = parseInt(addBody.get('quantity'), 10) || 1;
          } else if (typeof addBody === 'string') {
            var params = new URLSearchParams(addBody);
            vidGuess = parseInt(params.get('id'), 10);
            qtyGuess = parseInt(params.get('quantity'), 10) || 1;
          }
          if (vidGuess && cartData) {
            var existing = cartData.items.find(function (i) { return i.variant_id === vidGuess; });
            if (existing) {
              var newQty = existing.quantity + qtyGuess;
              var optItems = cartData.items.map(function (i) {
                if (i.variant_id !== vidGuess) return i;
                return Object.assign({}, i, {
                  quantity: newQty,
                  final_line_price:    (i.final_price || i.price || 0) * newQty,
                  original_line_price: (i.price || 0) * newQty,
                });
              });
              var optCart = Object.assign({}, cartData, {
                items: optItems,
                item_count: optItems.reduce(function (s, i) { return s + i.quantity; }, 0),
              });
              cartData = optCart;
              updateHeaderCount(optCart.item_count);
              render(optCart);
            }
          }
        } catch (e) { /* swallow — fall back to post-response render */ }

        // Clone the response so we can read the JSON without consuming it for the caller
        result.then(function (res) {
          if (!res.ok) return;
          if (addReqId !== _cartReqSeq) return; // stale — newer cart action ran
          var cloned;
          try { cloned = res.clone(); } catch (e) { cloned = null; }
          if (!cloned) {
            fetchCart().then(function (cart) {
              if (addReqId !== _cartReqSeq) return;
              cartData = cart;
              updateHeaderCount(cart.item_count);
              render(cart);
            }).catch(function () {});
            return;
          }
          cloned.json().then(function (added) {
            if (addReqId !== _cartReqSeq) return;
            if (!added || !added.key || !cartData) {
              fetchCart().then(function (cart) {
                if (addReqId !== _cartReqSeq) return;
                cartData = cart;
                updateHeaderCount(cart.item_count);
                render(cart);
              }).catch(function () {});
              return;
            }
            // Merge the added line directly — no second round-trip needed
            var existing = cartData.items.find(function (i) { return i.key === added.key; });
            var nextItems;
            if (existing) {
              nextItems = cartData.items.map(function (i) { return i.key === added.key ? added : i; });
            } else {
              nextItems = cartData.items.concat([added]);
            }
            var merged = Object.assign({}, cartData, {
              items: nextItems,
              item_count: nextItems.reduce(function (s, i) { return s + i.quantity; }, 0),
            });
            cartData = merged;
            updateHeaderCount(merged.item_count);
            render(merged);
          }).catch(function () {
            if (addReqId !== _cartReqSeq) return;
            fetchCart().then(function (cart) {
              if (addReqId !== _cartReqSeq) return;
              cartData = cart;
              updateHeaderCount(cart.item_count);
              render(cart);
            }).catch(function () {});
          });
        }).catch(function () {});
      }

      return result;
    };
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    // Parse upsell JSON embedded by Liquid
    var dataEl = document.getElementById('fc-cart-upsell-data');
    if (dataEl) {
      try {
        upsellData = JSON.parse(dataEl.textContent).upsells || [];
      } catch (e) {
        console.warn('[FC Cart] upsell parse error', e);
      }
    }

    var overlay  = eid('fc-cart-overlay');
    var closeBtn = eid('fc-cart-close');
    if (overlay)  overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
    });

    // Cart icon trigger
    document.querySelectorAll('[data-fc-cart-open]').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });

    // Custom event (any code can dispatch this to open the cart)
    document.addEventListener('fluffco:cart:open', function () { open(); });

    // Dawn pubsub cart update (fires after their product-form adds to cart)
    if (window.subscribe) {
      window.subscribe('cart-update', function () {
        // Skip if we're mid-change — fetchCart could return stale state that
        // overwrites our optimistic remove and makes the item briefly reappear.
        if (_changeInFlight > 0) return;
        fetchCart().then(function (cart) {
          if (_changeInFlight > 0) return;
          cartData = cart;
          updateHeaderCount(cart.item_count);
          open(cart);
        }).catch(function () {});
      });
    }

    // Dawn DOM event fallback
    document.addEventListener('cart:refresh', function () {
      if (_changeInFlight > 0) return;
      fetchCart().then(function (cart) {
        if (_changeInFlight > 0) return;
        cartData = cart;
        updateHeaderCount(cart.item_count);
        if (isOpen) render(cart);
      }).catch(function () {});
    });

    // Intercept fetch for universal ATC detection
    hookFetchForATC();

    // Warm the cache so first open is instant; also kick off discount prefetch.
    // Strip any stale GWP items left behind by an abandoned checkout.
    fetchCart()
      .then(cleanupStaleGwps)
      .then(function (cart) {
        cartData = cart;
        updateHeaderCount(cart.item_count);
        maybePrefetchCode(cart);
      })
      .catch(function () {});
  }

  // ─── Public API ──────────────────────────────────────────────────────────────
  window.FluffcoCart = { open: open, close: close };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
